import { Eye, EyeOff } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAuthStore } from '../../auth/auth-store'
import { RivenaLockup } from '../../components/brand/rivena-mark'
import { LanguageSwitcher } from '../../components/language-switcher'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { useAuth } from '../../auth/use-auth'
import { defaultRouteFor } from '../../layout/nav-items'
import { ThemeToggle } from '../../theme/theme-toggle'

interface FieldErrors {
  email?: string
  password?: string
}

/** Deliberately permissive. The server is the authority; this only catches typos. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface LocationState {
  from?: { pathname?: string }
}

export function LoginPage() {
  const { t } = useTranslation()
  const { login, isSubmitting, errorKey, clearError } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [tenantSlug, setTenantSlug] = useState('')
  const [showTenantField, setShowTenantField] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  const validate = (): boolean => {
    const errors: FieldErrors = {}
    if (email.trim().length === 0) errors.email = t('auth:validation.emailRequired')
    else if (!EMAIL_PATTERN.test(email.trim())) errors.email = t('auth:validation.emailInvalid')
    if (password.length === 0) errors.password = t('auth:validation.passwordRequired')

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    clearError()
    if (!validate()) return

    const slug = tenantSlug.trim().toLowerCase()
    const signedIn = await login({
      email: email.trim(),
      password,
      // Omitted rather than sent empty: the API treats an absent slug as "resolve
      // by e-mail alone", and an empty string would be a slug that matches nothing.
      ...(slug.length > 0 ? { tenantSlug: slug } : {}),
    })
    if (!signedIn) {
      // A failed sign-in is the only moment the ambiguity can surface, since the
      // API deliberately will not say "this address exists in two organisations".
      // Revealing the field here costs a correct single-tenant user nothing and
      // gives a multi-tenant user the one control that can resolve it.
      setShowTenantField(true)
      return
    }

    // Back to wherever the user was heading before the gate stopped them. Only a
    // path from history state is honoured — never a URL from the query string,
    // which an attacker could point at another origin.
    const intended = (location.state as LocationState | null)?.from?.pathname
    const roles = useAuthStore.getState().user?.roles ?? []
    navigate(intended && intended !== '/login' ? intended : defaultRouteFor(roles), {
      replace: true,
    })
  }

  return (
    <div className="flex min-h-full flex-col bg-background">
      <div className="flex justify-end gap-1 p-3">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      <div className="flex flex-1 items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm">
          <div className="mb-7 flex flex-col items-center gap-3 text-center">
            <RivenaLockup className="h-8" />
            <p className="text-sm text-foreground-muted">{t('common:tagline')}</p>
          </div>

          <div className="rounded-xl border border-border bg-surface p-6 shadow-card">
            <h1 className="text-lg font-semibold text-foreground">{t('auth:login.title')}</h1>
            <p className="mt-0.5 text-sm text-foreground-muted">{t('auth:login.subtitle')}</p>

            <form onSubmit={onSubmit} noValidate className="mt-5 space-y-4">
              {errorKey ? (
                <div
                  role="alert"
                  className="rounded-md border border-danger/30 bg-danger-subtle px-3 py-2 text-sm text-danger"
                >
                  {t(errorKey)}
                </div>
              ) : null}

              <Input
                label={t('auth:login.email')}
                type="email"
                name="email"
                autoComplete="username"
                inputMode="email"
                autoFocus
                placeholder={t('auth:login.emailPlaceholder')}
                value={email}
                error={fieldErrors.email ?? null}
                onChange={(event) => {
                  setEmail(event.target.value)
                  if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: undefined }))
                }}
              />

              <Input
                label={t('auth:login.password')}
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                placeholder={t('auth:login.passwordPlaceholder')}
                value={password}
                error={fieldErrors.password ?? null}
                onChange={(event) => {
                  setPassword(event.target.value)
                  if (fieldErrors.password)
                    setFieldErrors((prev) => ({ ...prev, password: undefined }))
                }}
                trailing={
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={
                      showPassword ? t('auth:login.hidePassword') : t('auth:login.showPassword')
                    }
                    className="flex h-7 w-7 items-center justify-center rounded text-foreground-subtle transition-colors hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff aria-hidden className="h-4 w-4" />
                    ) : (
                      <Eye aria-hidden className="h-4 w-4" />
                    )}
                  </button>
                }
              />

              {showTenantField ? (
                <Input
                  label={t('auth:login.organisation')}
                  type="text"
                  name="tenantSlug"
                  autoComplete="organization"
                  placeholder={t('auth:login.organisationPlaceholder')}
                  hint={t('auth:login.organisationHint')}
                  value={tenantSlug}
                  onChange={(event) => setTenantSlug(event.target.value)}
                />
              ) : null}

              <Button type="submit" isLoading={isSubmitting} className="w-full justify-center">
                {isSubmitting ? t('auth:login.submitting') : t('auth:login.submit')}
              </Button>
            </form>
          </div>

          <p className="mt-4 text-center text-xs text-foreground-subtle">{t('auth:login.help')}</p>
        </div>
      </div>
    </div>
  )
}
